import flatMap from "lodash/flatMap"
import sortBy from "lodash/sortBy"

/**
 * Board sections (kanban columns, agile gantt bars, process swimlanes) of kanban / simple
 * projects. They used to be mission.sectionTags; each is now an action flagged isSection under
 * the board phase, next to the catch-all action that holds every task in no section. A task's
 * section is its actionId. The API mirrors these rules in api/action/action.section.js.
 *
 * Screens read sections through getSections(), shaped like the old tags (from/to,
 * colomnSortIndex, dependsOn), and write them through app.missionAddSection /
 * missionUpdateSection / missionDeleteSection.
 */

// The board's virtual column for the tasks in no section
export const CATCH_ALL_ID = "catch-all"

const CATCH_ALL_TITLES = ["mx-ai", "mx-ai-source", "General tasks..."]

export const isCatchAllAction = (action) =>
    !!action &&
    !action.isSection &&
    (action.source === "mx-ai" ||
        action.aiSource === "mx-ai" ||
        action.sourceAi === "mx-ai" ||
        CATCH_ALL_TITLES.includes(action.title))

const getPhases = (mission) => (mission?.planItems || []).filter((p) => p.type === "phase")

/** The phase holding the catch-all (else the first phase): where sections are created */
export const getBoardPhase = (mission) => {
    const phases = getPhases(mission)

    return phases.find((p) => (p.actions || []).some(isCatchAllAction)) || phases[0]
}

/** The catch-all action new tasks in no section go under */
export const getBoardAction = (mission) =>
    (getBoardPhase(mission)?.actions || []).find(isCatchAllAction) ||
    flatMap(mission?.planItems || [], "actions").find(isCatchAllAction)

const idOf = (ref) => (ref && typeof ref === "object" ? ref._id : ref)

/** A section action in the shape the screens use (the old section tag's) */
export const toSection = (action) => ({
    _id: action._id,
    title: action.title,
    color: action.color,
    from: action.startDate,
    to: action.endDate,
    colomnSortIndex: action.position || 0,
    sortIndex: action.position || 0,
    dependsOn: (action.dependencies || []).map((d) => idOf(d.action)).filter(Boolean),
    defaultAiCode: action.defaultAiCode,
    templateId: action.templateId,
    orderCode: action.orderCode,
    estimatedHours: action.estHours,
    missionId: action.missionId,
    missionPlanId: action.missionPlanId,
    action,
})

// Per mission id: the section actions last seen and their shapes, so screens get the same array
// back until a section changes, across copies of the mission (some state updates mutate
// planItems in place, so the cache checks the actions themselves)
const cache = new Map()
const EMPTY = { actions: [], sections: [] }

const getCached = (mission) => {
    if (!mission?.planItems) return EMPTY

    const actions = flatMap(mission.planItems, "actions").filter((a) => a?.isSection)
    const hit = cache.get(mission._id ?? mission)

    if (hit && hit.raw.length === actions.length && hit.raw.every((a, i) => a === actions[i])) {
        return hit
    }

    const sorted = sortBy(actions, [(a) => a.position || 0, (a) => a.createdAt || ""])
    const entry = { raw: actions, actions: sorted, sections: sorted.map(toSection) }

    cache.set(mission._id ?? mission, entry)

    return entry
}

/** Section actions in board order */
export const getSectionActions = (mission) => getCached(mission).actions

/** The mission's sections, in board order, shaped like the old section tags */
export const getSections = (mission) => getCached(mission).sections

export const getSection = (mission, sectionId) => getSections(mission).find((s) => s._id === sectionId)

export const isSectionAction = (mission, actionId) => !!actionId && !!getSection(mission, actionId)

/** The section a task is in, or undefined for the catch-all */
export const getTaskSectionId = (mission, ai) => (isSectionAction(mission, ai?.actionId) ? ai.actionId : undefined)

/** Does the task belong to this section (or, for CATCH_ALL_ID, to none)? */
export const isTaskInSection = (mission, ai, sectionId) =>
    sectionId === CATCH_ALL_ID ? !getTaskSectionId(mission, ai) : ai?.actionId === sectionId

/** The action a task created in a section (or the catch-all column) goes under */
export const getSectionTaskAction = (mission, sectionId) =>
    sectionId && sectionId !== CATCH_ALL_ID
        ? getSectionActions(mission).find((a) => a._id === sectionId)
        : getBoardAction(mission)

/** A section change, in the old tag fields the screens send, as an action change */
export const toActionChange = (change = {}) => {
    const out = {}

    if ("title" in change) out.title = change.title
    if ("color" in change) out.color = change.color
    if ("from" in change) out.startDate = change.from
    if ("to" in change) out.endDate = change.to
    if ("colomnSortIndex" in change) out.position = change.colomnSortIndex
    else if ("sortIndex" in change) out.position = change.sortIndex
    if ("defaultAiCode" in change) out.defaultAiCode = change.defaultAiCode
    if ("templateId" in change) out.templateId = change.templateId
    if ("orderCode" in change) out.orderCode = change.orderCode
    if ("estimatedHours" in change) out.estHours = change.estimatedHours

    return out
}
